/**
 * The receivables, inside Connect TallyPrime › "Check the totals from
 * TallyPrime" (docs/tally/20 §1 and §5): before anything is written, what Timely will make of
 * what it read — the ageing (and the windows to read it by), how the net is
 * made up, and the accounting checks.
 *
 * Everything comes from the stored preview: the company's figures for anyone
 * who may view receivables data, and each account's checks only for those who
 * manage it (the preview's account list is withheld from everyone else).
 *
 * A company connecting for the first time is asked how its receivables are
 * aged (`mustChoose`): neither basis is assumed, the answer is previewed here,
 * and Connect saves it before anything is written (docs/tally/18 §1.3).
 */
import { useMemo } from 'react';
import type { PreviewIntelligence, ReceivablesSettings } from '@timely/domain';
import type { PreviewAccount } from '../datasource/useConnector';
import { AgeingEditor } from './AgeingEditor';
import { CheckCounts, ChecksList, previewFindings } from './AccountingChecks';
import { CompositionTable } from './ReceivablesFigures';
import type { useReceivablesSettings } from './useReceivablesSettings';

export function PreviewReceivables({ intelligence, accounts, canManage, settings, mustChoose = false, onDraft }: {
    intelligence: PreviewIntelligence;
    accounts: PreviewAccount[] | undefined;
    canManage: boolean;
    settings: ReturnType<typeof useReceivablesSettings>;
    /** Connecting a company that has not chosen how its receivables are aged: ask, and let Connect save the answer. */
    mustChoose?: boolean;
    onDraft?: (draft: ReceivablesSettings | null) => void;
}) {
    const findings = useMemo(() => previewFindings({ companyFindings: intelligence.companyFindings, accounts }), [intelligence, accounts]);
    // Start from what the company saved, if anything; otherwise from what the preview was computed with.
    const saved: ReceivablesSettings = settings.available && settings.current.version > 0 ? settings.current.settings : intelligence.settings;
    const onSave = canManage && settings.available && !mustChoose
        ? async (next: ReceivablesSettings, reason: string, impact: Record<string, unknown>) => { await settings.save(next, reason, impact); }
        : undefined;

    return (
        <div className="mt-6 grid gap-7">
            <section aria-labelledby="preview-ageing">
                <h4 id="preview-ageing" className="text-body font-semibold text-label">Ageing</h4>
                <p className="text-secondary text-label-3 max-w-prose">
                    The open bills by age, as TallyPrime’s own Age-wise report shows them. Credit notes, advances and money received On Account are listed apart: they are against no bill yet, so Timely does not put them in a bucket.
                </p>
                <div className="mt-3 -mx-5 sm:-mx-6">
                    {!settings.loading && (
                        <AgeingEditor
                            histogram={intelligence.histogram}
                            position={intelligence.position}
                            saved={saved}
                            chooseBasis={mustChoose}
                            {...(mustChoose && onDraft ? { onDraft } : {})}
                            {...(onSave ? { onSave } : {})}
                            note={mustChoose
                                ? 'Connecting saves your choice, with these windows, as the company’s first version; you can change it later in Settings.'
                                : onSave ? undefined : settings.available
                                    ? 'Somebody who manages receivables data can save these windows for the company.'
                                    : 'A preview: until ageing settings can be saved for this company, Timely uses 0–45, 46–90, 91–135 and over 135 days by bill date.'}
                        />
                    )}
                </div>
            </section>

            <section aria-labelledby="preview-net">
                <h4 id="preview-net" className="text-body font-semibold text-label">How the net is made up</h4>
                <div className="mt-3 -mx-5 sm:-mx-6"><CompositionTable position={intelligence.position} /></div>
            </section>

            <section aria-labelledby="preview-checks">
                <h4 id="preview-checks" className="text-body font-semibold text-label">What Timely found</h4>
                <p className="text-secondary text-label-3 max-w-prose">
                    Ordinary accounting that affects how clearly each amount can be collected on. Nothing here changes TallyPrime; when something is changed there, the next update shows it.
                </p>
                <div className="mt-3"><CheckCounts summary={intelligence.findings} /></div>
                <div className="mt-3"><ChecksList summary={intelligence.findings} findings={findings} accountsHidden={!accounts} /></div>
            </section>
        </div>
    );
}
